import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Flame, CheckCircle2, CalendarClock, Send, TrendingUp, ArrowRight, Radar as RadarIcon,
  CalendarDays, MapPin, Clock, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAccount } from "@/context/AccountContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, FlagBadges, HeatBadge } from "@/components/Badges";
import { thumbUrl } from "@/components/FileUpload";
import { PIPELINE_COLUMNS } from "@/lib/constants";

export default function Dashboard() {
  const { accountKey } = useOutletContext();
  const { current } = useAccount();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!accountKey) return;
    api.get("/dashboard", { params: { account: accountKey } }).then(({ data }) => setData(data));
  }, [accountKey]);

  if (user?.role === "creator") return <CreatorDashboard accountKey={accountKey} current={current} user={user} navigate={navigate} />;

  if (!data) return <div className="p-8 text-neutral-400">Lade Dashboard…</div>;

  const k = data.kpis;
  const kpis = [
    { label: "Offene Freigaben", value: k.open_approvals, icon: CheckCircle2, tone: "amber", to: "/freigabe" },
    { label: "Heiße Themen", value: k.hot_topics, icon: Flame, tone: "red", to: "/themenradar" },
    { label: "Geplant", value: k.scheduled, icon: CalendarClock, tone: "teal", to: "/kalender" },
    { label: "Veröffentlicht", value: k.published, icon: Send, tone: "neutral", to: "/pipeline" },
  ];

  const toneMap = {
    amber: "text-orange-600 bg-orange-100/70",
    red: "text-red-600 bg-red-100/70",
    teal: "text-emerald-600 bg-emerald-100/70",
    neutral: "text-slate-700 bg-slate-100/80",
  };

  const today = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date());

  return (
    <div className="liquid-dashboard px-4 py-6 md:px-7 md:py-9 max-w-[1480px] mx-auto animate-rise">
      <div className="flex items-end justify-between flex-wrap gap-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{current?.name}</p>
          <h1 className="font-head text-[clamp(2rem,3.4vw,3.25rem)] leading-[1.04] font-bold text-slate-950 tracking-[-0.045em] mt-2">
            Hallo {user?.name?.split(" ")[0]}, hier ist deine Lage.
          </h1>
        </div>
        <div className="hidden md:block text-right pb-1">
          <p className="text-[14px] font-semibold text-slate-700 capitalize">{today}</p>
          <p className="text-[13px] text-slate-500 mt-1">Schön, dass ihr da seid.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.55fr_repeat(3,1fr)] gap-4 mt-7">
        {kpis.map((kpi) => (
          <button
            key={kpi.label}
            data-testid={`kpi-${kpi.label}`}
            onClick={() => navigate(kpi.to)}
            className={`liquid-card liquid-kpi text-left min-h-[132px] p-5 sm:p-6 hover:-translate-y-0.5 transition-all group ${kpi.tone === "amber" ? "liquid-kpi-priority" : ""}`}
          >
            <div className="flex items-center gap-4">
              <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${toneMap[kpi.tone]}`}>
                <kpi.icon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <div className="font-head text-[32px] leading-none font-bold text-slate-950">{kpi.value}</div>
                <div className="text-[15px] font-semibold text-slate-800 mt-1.5">{kpi.label}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pl-[60px] gap-3">
              <span className="text-xs text-slate-500">{kpi.tone === "amber" ? "Warten auf eure Prüfung" : kpi.tone === "red" ? "Aktuell im Trend" : kpi.tone === "teal" ? "Für die nächsten 7 Tage" : "In den letzten 30 Tagen"}</span>
              {kpi.tone === "amber" ? (
                <span className="shrink-0 rounded-full bg-[#FF6B35] px-3 py-1.5 text-[11px] font-bold text-white shadow-sm group-hover:bg-[#F05A24] transition-colors">
                  Jetzt prüfen
                </span>
              ) : (
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:translate-x-1 transition-transform" />
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.9fr)_minmax(320px,1fr)] gap-5 mt-5">
        {/* Hot topics */}
        <div className="liquid-card p-5 sm:p-6">
          <SectionHead icon={Flame} title="Top-Themen" onMore={() => navigate("/themenradar")} />
          <div className="mt-4 divide-y divide-neutral-100">
            {data.hot_topics.length === 0 && <Empty text="Keine heißen Themen." />}
            {data.hot_topics.map((t) => (
              <div key={t.id} className="py-4 flex items-start gap-3.5">
                <span className="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 shadow-sm" style={{ background: t.heat === "hot" ? "#FF3B30" : "#FF9F0A" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-slate-900 text-[15px]">{t.title}</h4>
                    <HeatBadge heat={t.heat} />
                  </div>
                  <p className="text-[13px] text-slate-500 mt-1 line-clamp-1">{t.summary}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 mt-2 shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Creator status */}
        <div className="space-y-5">
        <div className="liquid-card p-5 sm:p-6">
          <SectionHead icon={Users} title="Creator-Status" />
          <div className="mt-4 space-y-3">
            {data.creator_status.length === 0 && <Empty text="Keine Creator zugeordnet." />}
            {data.creator_status.map((c) => (
              <div key={c.name} className="flex items-center gap-3 py-1">
                <span className="h-10 w-10 rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm" style={{ background: c.avatar_color }}>
                  {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-neutral-800">{c.name}</div>
                  <div className="text-xs text-neutral-400">{c.active_tasks} aktive Aufgabe(n)</div>
                </div>
                <span className={`h-2.5 w-2.5 rounded-full ${c.active_tasks > 0 ? "bg-emerald-500" : "bg-slate-300"}`} />
              </div>
            ))}
          </div>

        </div>
        <div className="liquid-card p-5 sm:p-6">
            <SectionHead icon={TrendingUp} title={data.instagram?.connected ? "Performance · live" : "Performance"} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              {data.instagram?.connected ? (
                <>
                  <Stat label="Follower" value={(data.instagram.followers ?? 0).toLocaleString("de-DE")} />
                  <Stat label="IG-Views · 30 Tage" value={(data.instagram.account_30d?.views ?? data.instagram.avg_engagement ?? 0).toLocaleString("de-DE")} />
                  <Stat label="Reichweite · 30 T" value={(data.instagram.account_30d?.reach ?? 0).toLocaleString("de-DE")} />
                  <Stat label="Bestes Format" value={data.instagram.best_format || "—"} />
                </>
              ) : (
                <>
                  <Stat label="Ø Reichweite" value={data.performance.avg_reach.toLocaleString("de-DE")} />
                  <Stat label="Bestes Format" value={data.performance.best_format} />
                </>
              )}
            </div>
            {data.instagram?.connected && (
              <p className="text-[11px] text-neutral-400 mt-2">Echte Instagram-Daten · @{data.instagram.username} · nur Instagram, ohne Facebook-Crossposting</p>
            )}
        </div>
        </div>
      </div>

      {/* Anstehende Events */}
      <div className="liquid-card p-5 sm:p-6 mt-5">
        <SectionHead icon={CalendarDays} title="Anstehende Events" onMore={() => navigate("/event-radar")} />
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {(!data.upcoming_events || data.upcoming_events.length === 0) && <Empty text="Keine anstehenden Events. Synchronisiere im Event-Radar." />}
          {(data.upcoming_events || []).map((e) => (
            <button
              key={e.id}
              data-testid={`dashboard-event-${e.id}`}
              onClick={() => navigate("/event-radar")}
              className="liquid-inset text-left flex gap-3 p-3.5 hover:bg-white/75 transition"
            >
              {e.image_url ? (
                <img src={e.image_url} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0 border border-neutral-200" />
              ) : (
                <div className="h-14 w-14 rounded-2xl shrink-0 bg-slate-100/80 flex flex-col items-center justify-center">
                  <span className="font-head text-lg font-bold text-neutral-800 leading-none">{e.date?.slice(8, 10)}</span>
                  <span className="text-[9px] uppercase text-neutral-400">{e.date?.slice(5, 7)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-900 text-sm truncate">{e.title}</div>
                <div className="text-xs text-neutral-400 mt-1 flex items-center gap-1 truncate">
                  <CalendarDays className="h-3 w-3 shrink-0" /> {e.date}{e.time ? <><Clock className="h-3 w-3 ml-1 shrink-0" /> {e.time}</> : null}
                </div>
                {e.location && <div className="text-xs text-neutral-400 mt-0.5 flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {e.location}</div>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Open approvals + pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="lg:col-span-2 liquid-card p-5 sm:p-6">
          <SectionHead icon={CheckCircle2} title="Wartet auf Freigabe" onMore={() => navigate("/freigabe")} />
          <div className="mt-4 space-y-2.5">
            {data.open_approvals.length === 0 && <Empty text="Alles freigegeben — top!" />}
            {data.open_approvals.map((c) => (
              <button
                key={c.id}
                data-testid={`approval-row-${c.id}`}
                onClick={() => navigate("/freigabe")}
                className="liquid-inset w-full text-left flex items-center gap-3 p-3.5 hover:bg-white/75 transition"
              >
                {c.media_url && <img src={thumbUrl(c.media_url, 160)} alt="" className="h-12 w-10 rounded-md object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-900 text-sm truncate">{c.title}</div>
                  <div className="mt-1"><FlagBadges flags={c.flags} /></div>
                </div>
                <StatusBadge status={c.status} />
                <ArrowRight className="h-4 w-4 text-neutral-300" />
              </button>
            ))}
          </div>
        </div>

        <div className="liquid-card p-5 sm:p-6">
          <SectionHead icon={RadarIcon} title="Pipeline-Überblick" onMore={() => navigate("/pipeline")} />
          <div className="mt-4 space-y-2.5">
            {PIPELINE_COLUMNS.map((col) => {
              const count = col.statuses.reduce((s, st) => s + (data.pipeline_counts[st] || 0), 0);
              const max = Math.max(...Object.values(data.pipeline_counts), 1);
              return (
                <div key={col.key} className="flex items-center gap-3">
                  <span className="text-xs text-neutral-500 w-28 shrink-0">{col.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full bg-neutral-800 transition-all" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-neutral-700 w-5 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ icon: Icon, title, onMore }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="font-head text-[17px] font-bold text-slate-950 flex items-center gap-2.5">
        {Icon && <Icon className="h-5 w-5 text-slate-700" />} {title}
      </h3>
      {onMore && (
        <button data-testid={`dashboard-more-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={onMore} className="text-[13px] font-semibold text-[#007AFF] hover:opacity-75 flex items-center gap-1.5">
          Alle anzeigen <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="liquid-inset p-3.5">
      <div className="font-head text-xl font-bold text-slate-950">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-neutral-400 py-4">{text}</p>;
}

function CreatorDashboard({ accountKey, current, user, navigate }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!accountKey) return;
    api.get("/content", { params: { account: accountKey } }).then(({ data }) =>
      setItems(data.filter((c) => c.assigned_creator === user?.name)),
    );
  }, [accountKey, user]);

  const open = items.filter((c) => ["idea", "research", "draft", "creator_working"].includes(c.status));
  const inReview = items.filter((c) => c.status === "review");
  const done = items.filter((c) => ["approved", "scheduled", "published"].includes(c.status));

  const groups = [
    { label: "In Arbeit", list: open, tone: "indigo" },
    { label: "Im Review", list: inReview, tone: "amber" },
    { label: "Abgeschlossen", list: done, tone: "green" },
  ];
  const toneDot = { indigo: "bg-indigo-500", amber: "bg-amber-500", green: "bg-green-500" };

  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto animate-rise">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">{current?.name}</p>
      <h1 className="font-head text-4xl font-bold text-neutral-900 tracking-tighter mt-1">
        Hallo {user?.name?.split(" ")[0]}, deine Aufgaben.
      </h1>

      <div className="grid grid-cols-3 gap-4 mt-6">
        {groups.map((g) => (
          <div key={g.label} className="bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-5">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${toneDot[g.tone]}`} />
              <span className="text-sm text-neutral-500">{g.label}</span>
            </div>
            <div className="font-head text-3xl font-bold text-neutral-900 mt-2">{g.list.length}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <button data-testid="creator-open-companion" onClick={() => navigate("/companion")}
          className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-blue-700">
          Mobile-Companion öffnen <ArrowRight className="h-4 w-4" />
        </button>
        <button onClick={() => navigate("/creator-aufgaben")}
          className="inline-flex items-center gap-2 bg-white border border-neutral-200 text-neutral-800 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-50">
          Alle Aufgaben
        </button>
      </div>

      <div className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
        <SectionHead title="Meine Inhalte" onMore={() => navigate("/pipeline")} />
        <div className="mt-4 space-y-2.5">
          {items.length === 0 && <Empty text="Dir sind aktuell keine Inhalte zugewiesen." />}
          {items.map((c) => (
            <button key={c.id} data-testid={`creator-item-${c.id}`} onClick={() => navigate("/pipeline")}
              className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-neutral-100 hover:bg-neutral-50 transition">
              {c.media_url && <img src={thumbUrl(c.media_url, 160)} alt="" className="h-11 w-9 rounded object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-900 text-sm truncate">{c.title}</div>
                <div className="mt-1"><FlagBadges flags={c.flags} /></div>
              </div>
              <StatusBadge status={c.status} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
